import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../../core/AppContext';

/**
 * Two panels for the TrueFoundry-VM lifecycle, kept out of the giant ModelEvalView:
 *
 *  <VmMembersPanel/>   — tab ④ "TrueFoundry VM": only models ACTUALLY serving behind a
 *                        VM (vm_placements present), grouped by VM, with a "Move to
 *                        Challenger" action.
 *  <ChallengerArena/>  — tab ⑤ "Challenger": the arena of models lined up to replace a
 *                        VM incumbent. Each carries a REASON + CRITERIA (why it earns the
 *                        slot). Includes the 4 self-hosted PoC. Move a challenger into the
 *                        VM (push) or remove it; stage any EchoStar/Other model as a
 *                        challenger from the picker.
 */

const VM_META = {
  'echostar/fast-v1': { color: '#0073bb', blurb: 'High-volume chat / classify / lookup' },
  'echostar/smart-v1': { color: '#28a745', blurb: 'Core enterprise agents / structured JSON' },
  'echostar/balanced-v1': { color: '#6f42c1', blurb: 'Deep reasoning / complex coding' },
};
const SOURCE_BADGE = {
  echostar: { bg: '#d4edda', fg: '#155724', label: 'EchoStar-approved' },
  other: { bg: '#fff3cd', fg: '#856404', label: 'Other (unvetted)' },
  self_hosted: { bg: '#e7f0ff', fg: '#0b5cad', label: 'Self-hosted PoC' },
};

// Deep link to the TrueFoundry Models page (where "Add Model" → "Apply using YAML" lives).
const TF_MODELS_URL = 'https://echostar-tsa.truefoundry.cloud/llm-gateway/models';

// Best-effort Bedrock-style platform id suggestion from an EEA catalog id.
// e.g. 'anthropic/claude-haiku-5.5' -> 'global.anthropic.claude-haiku-5-5'.
// This is a SUGGESTION only — the user must verify it is the exact platform model id.
function suggestPlatformId(mId) {
  if (!mId) return '<platform-model-id>';
  const provider = mId.includes('/') ? mId.split('/')[0] : 'anthropic';
  const part = (mId.includes('/') ? mId.split('/').slice(1).join('/') : mId).replace(/\./g, '-');
  return `global.${provider}.${part}`;
}

// The exact integrations[] entry to ADD (guided enable). We never PUT this ourselves:
// enabling re-PUTs the WHOLE provider-account manifest (auth_data.api_key is required but
// redacted on read, and every existing model must stay in integrations[]), so a blind write
// would break auth or wipe models. EEA only shows the entry; the admin applies it in TF.
function manifestEntryFor(mId) {
  const pid = suggestPlatformId(mId);
  return [
    '  - type: integration/model/bedrock   # or integration/model/<provider>',
    `    name: ${pid}        # verify: exact platform model id (Bedrock/Vertex)`,
    `    model_id: ${pid}    # must match the real provider model id`,
    '    model_types:',
    '      - chat',
    '    cost:',
    '      metric: public_cost',
  ].join('\n');
}

function placementsOf(item) {
  return item.vm_placements || {};
}

// ---- Tab ④ : models actually serving behind a VM ---------------------------
export function VmMembersPanel() {
  const { echostarCatalog, otherCatalog, handleChallengerAdd, handleVmFlip, vmStatus, can, fmtCostPerMCell, getAuthHeaders, fetchCatalogs } = useApp();
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [syncEnv, setSyncEnv] = useState('sandbox');
  const [liveVms, setLiveVms] = useState(null);  // real VM structure from TrueFoundry
  const [syncedEpoch, setSyncedEpoch] = useState(null);
  const [abPick, setAbPick] = useState({});   // vmName -> challenger catalog model_id for the A/B control

  // On open, load the LAST persisted VM state from the DB so the tab isn't blank
  // until a manual sync. 'Sync from live' then refreshes both the DB and this view.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/truefoundry-vm-state', { headers: getAuthHeaders() });
        const data = await res.json().catch(() => ({}));
        if (alive && res.ok && Array.isArray(data.live_vms)) {
          setLiveVms(data.live_vms);
          setSyncedEpoch(data.synced_epoch || null);
        }
      } catch (e) { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [getAuthHeaders]);

  // Pull live VM membership from the TrueFoundry sandbox and reflect it into the
  // VM tab, so this list mirrors what is actually serving right now.
  const syncFromLive = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const res = await fetch('/api/truefoundry-vm-sync', {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: syncEnv }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setSyncMsg(data.error || 'Sync failed.'); }
      else if (data.status === 'NOT_CONFIGURED') { setSyncMsg(data.message || 'TrueFoundry not configured.'); }
      else if (data.status === 'READ_FAILED') { setSyncMsg(data.message || 'Could not read VMs from TrueFoundry.'); }
      else {
        setLiveVms(data.live_vms || []);
        setSyncedEpoch(Math.floor(Date.now() / 1000));
        setSyncMsg(data.message || 'Synced.');
        if (typeof fetchCatalogs === 'function') fetchCatalogs();
      }
    } catch (e) { setSyncMsg('Network error contacting the server.'); }
    finally { setSyncing(false); }
  };

  // Catalog models offered as A/B challengers (short EEA ids; the flip engine
  // resolves each to its real gateway fqn when it ramps).
  const abCatalog = [...(echostarCatalog || []), ...(otherCatalog || [])]
    .map(it => it.model_id || it.id).filter(Boolean).sort();
  // Run one A/B flip op, then re-sync so the live weight split refreshes in place.
  const runFlip = async (vmName, op) => {
    const mId = abPick[vmName];
    if (op === 'start' && !mId) { setSyncMsg('Pick a challenger model first.'); return; }
    await handleVmFlip(mId, syncEnv, op, vmName);
    syncFromLive();
  };

  return (
    <div>
      <h3 style={{ marginBottom: '4px' }}>④ TrueFoundry VM — live membership</h3>
      <p style={{ color: '#666', fontSize: '13px', marginTop: 0 }}>
        Only models <strong>actually pushed into a virtual model</strong> (not every approved model).
        End users call the alias, never these backends — so swapping a target here is invisible to them.
        Use <strong>Move to Challenger</strong> to line a serving model up for replacement/comparison.
      </p>
      {can('push_truefoundry') && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', background: '#f0f7ff', border: '1px solid #b6d4fe', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '12px', color: '#084298' }}>🔄 <strong>Sync from live TrueFoundry</strong> — read the sandbox VMs' real targets and mirror them here.</span>
          <label style={{ fontSize: '11px', color: '#084298', marginLeft: 'auto' }}>Env:</label>
          <select value={syncEnv} onChange={e => setSyncEnv(e.target.value)} style={{ fontSize: '12px', padding: '5px', borderRadius: '4px' }}>
            <option value="sandbox">sandbox</option><option value="np">np</option><option value="prod">prod</option>
          </select>
          <button onClick={syncFromLive} disabled={syncing}
                  style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '20px', cursor: syncing ? 'default' : 'pointer', fontWeight: 'bold' }}>
            {syncing ? 'Syncing…' : '🔄 Sync from live'}
          </button>
        </div>
      )}
      {syncMsg && <p style={{ fontSize: '12px', color: '#084298', background: '#eef3f8', borderRadius: '4px', padding: '6px 10px' }}>{syncMsg}</p>}

      {/* LIVE view: exactly what TrueFoundry currently has (real VM names + targets),
          independent of the EEA catalog. Populated by "Sync from live". */}
      {Array.isArray(liveVms) && (
        <div style={{ marginBottom: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#138496', margin: '4px 0 8px' }}>
            Live in TrueFoundry ({liveVms.length} virtual model{liveVms.length === 1 ? '' : 's'})
            {syncedEpoch && <span style={{ fontWeight: 'normal', color: '#718096', marginLeft: '6px' }}>
              · last synced {new Date(syncedEpoch * 1000).toLocaleString()}</span>}
          </div>
          {liveVms.length === 0 && (
            <div style={{ color: '#888', fontSize: '12px' }}>TrueFoundry returned no virtual models for this account.</div>
          )}
          {liveVms.map((vm, i) => (
            <div key={i} style={{ border: '1px solid #bee3e8', borderRadius: '6px', padding: '10px 12px', marginBottom: '8px', background: '#f3fbfc' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '13px', color: '#0b6b78' }}>{vm.vm}</strong>
                {vm.routing_type && <span style={{ fontSize: '10px', color: '#555', border: '1px solid #cbd5e0', borderRadius: '8px', padding: '1px 6px' }}>{vm.routing_type}</span>}
                <span style={{ fontSize: '11px', color: '#718096' }}>{(vm.targets || []).length} target(s)</span>
              </div>
              {(vm.targets || []).map((t, j) => (
                <div key={j} style={{ fontSize: '12px', color: '#333', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ wordBreak: 'break-all' }}>{t.model_id}</span>
                  <span style={{ fontSize: '10px', color: '#0b6b78' }}>{t.tier ? `tier: ${t.tier}` : (t.weight != null ? `${t.weight}%` : t.role)}</span>
                  {t.in_catalog
                    ? <span title={`Maps to EEA catalog model ${t.catalog_model_id}`} style={{ fontSize: '9px', color: '#155724', background: '#d4edda', borderRadius: '8px', padding: '1px 5px' }}>✓ catalog: {t.catalog_model_id}</span>
                    : <span title="This serving model is not in the EEA catalog — import it from the Market Scanner to track/compare it" style={{ fontSize: '9px', color: '#9a5a1b', background: '#fff3cd', borderRadius: '8px', padding: '1px 5px' }}>not in catalog</span>}
                  {t.in_catalog && can('assign_vm') && (
                    <button onClick={() => handleChallengerAdd(t.catalog_model_id, vm.vm)}
                            title="Line this serving model up in the Challenger arena"
                            style={{ padding: '2px 7px', fontSize: '10px', border: '1px solid #fd7e14', background: '#fff7ef', color: '#b35c00', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                      → Challenger
                    </button>
                  )}
                </div>
              ))}
              {/* Inline A/B — weight-based VMs only (the only kind that can traffic-split).
                  Complexity-based VMs route by task tier, not a % split, so no A/B here. */}
              {(vm.routing_type || '').includes('weight') ? (
                can('push_truefoundry') && (
                  <div style={{ marginTop: '8px', borderTop: '1px dashed #bee3e8', paddingTop: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#0b6b78', marginBottom: '4px' }}>
                      ⚖️ A/B flip (weight-based) · current split: {(vm.targets || []).map(t => `${t.weight != null ? t.weight : '—'}%`).join(' / ') || '—'}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <select value={abPick[vm.vm] || ''} onChange={e => setAbPick(p => ({ ...p, [vm.vm]: e.target.value }))}
                              style={{ fontSize: '11px', padding: '3px', borderRadius: '4px', maxWidth: '260px' }}>
                        <option value="">(pick a challenger to ramp in…)</option>
                        {abCatalog.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <button onClick={() => runFlip(vm.vm, 'start')} title="Add the challenger at 10% traffic"
                              style={{ fontSize: '11px', padding: '4px 9px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Start 10%</button>
                      <button onClick={() => runFlip(vm.vm, 'advance')} title="Advance to the next stage (10→50→100)"
                              style={{ fontSize: '11px', padding: '4px 9px', border: '1px solid #0073bb', background: '#eef6fc', color: '#084298', borderRadius: '4px', cursor: 'pointer' }}>Advance ▶</button>
                      <button onClick={() => runFlip(vm.vm, 'rollback')} title="Restore the weights from before the flip"
                              style={{ fontSize: '11px', padding: '4px 9px', border: '1px solid #dc3545', background: '#fdf0f1', color: '#b00020', borderRadius: '4px', cursor: 'pointer' }}>Roll back</button>
                      <button onClick={() => runFlip(vm.vm, 'complete')} title="Make the challenger the primary (old primary kept as 0% fallback)"
                              style={{ fontSize: '11px', padding: '4px 9px', border: '1px solid #28a745', background: '#eafaf0', color: '#155724', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Complete ✓</button>
                    </div>
                    <div style={{ fontSize: '10px', color: '#718096', marginTop: '4px' }}>Each step ramps the challenger and re-syncs; end users keep calling <code>{vm.vm}/{vm.vm}</code> throughout.</div>
                  </div>
                )
              ) : (
                <div style={{ fontSize: '10px', color: '#718096', marginTop: '6px', fontStyle: 'italic' }}>
                  Auto Routing (by task complexity) — routes per tier, not a traffic %, so A/B weight flips don't apply here.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Tab ⑤ : the challenger arena ------------------------------------------
export function ChallengerArena() {
  const { getAuthHeaders, can, echostarCatalog, otherCatalog, handleVmPush, handleChallengerAdd, handleChallengerRemove } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [env, setEnv] = useState('sandbox');
  const [pick, setPick] = useState('');
  const [ranking, setRanking] = useState(false);
  const [rankMsg, setRankMsg] = useState('');
  const [schedule, setSchedule] = useState('off');
  const [scheduleOpts, setScheduleOpts] = useState({ off: 'Off (manual only)', '30m': 'Every 30 minutes', '1h': 'Every hour', '6h': 'Every 6 hours', '1d': 'Once a day' });
  const [savingSched, setSavingSched] = useState(false);
  // Live TrueFoundry VMs (real names + whether tier-routed), for tier-aware promotion.
  const [vmList, setVmList] = useState([]);
  const [tierPick, setTierPick] = useState({});  // model_id -> chosen tier for a tier VM
  const [gatewayModels, setGatewayModels] = useState([]);  // real enabled target fqns
  const [targetPick, setTargetPick] = useState({});        // model_id -> explicit fqn override

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/challengers', { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      setItems(data.challengers || []);
    } catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // Load the admin-configured ranking cadence.
  const loadSchedule = useCallback(async () => {
    try {
      const res = await fetch('/api/challenger-schedule', { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.schedule) setSchedule(data.schedule);
        if (data.options) setScheduleOpts(data.options);
      }
    } catch (e) { /* non-fatal */ }
  }, [getAuthHeaders]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // Load the real TrueFoundry VMs so promotion can be tier-aware (and names are live).
  const loadVms = useCallback(async () => {
    try {
      const res = await fetch('/api/truefoundry-vms', { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.vms)) setVmList(data.vms);
    } catch (e) { /* non-fatal; promotion falls back to defaults */ }
  }, [getAuthHeaders]);
  useEffect(() => { loadVms(); }, [loadVms]);

  // Load the real enabled gateway target fqns so the UI can show which challengers
  // actually map to a pushable target (and let the user pick the exact fqn).
  const loadGatewayModels = useCallback(async () => {
    try {
      const res = await fetch('/api/truefoundry-gateway-models', { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.models)) setGatewayModels(data.models);
    } catch (e) { /* non-fatal */ }
  }, [getAuthHeaders]);
  useEffect(() => { loadGatewayModels(); }, [loadGatewayModels]);

  // Client-side mirror of resolve_target_model: normalized family-key suffix match.
  const normKey = (s) => (s || '').toLowerCase().replace(/\d{8}/g, '').replace(/[^a-z0-9]/g, '').replace(/v\d+$/, '');
  const resolveTarget = (mid) => {
    if (!mid) return { status: 'INVALID' };
    if (gatewayModels.includes(mid)) return { status: 'MATCH', target: mid };
    const key = normKey(mid);
    const hits = gatewayModels.filter(f => {
      const lk = normKey(f), mk = f.includes('/') ? normKey(f.split('/').slice(1).join('/')) : lk;
      return key === lk || key === mk || lk.endsWith(key) || mk.endsWith(key);
    });
    if (hits.length === 1) return { status: 'RESOLVED', target: hits[0] };
    if (hits.length > 1) return { status: 'AMBIGUOUS', candidates: hits };
    return { status: 'NOT_AVAILABLE' };
  };

  // Is this VM tier-routed (Simple/Medium/Complex)? Matches on real name or the
  // normalized EEA alias (echostar/smart-v1 -> smart). Unknown -> treat as weight.
  const vmIsTier = (vmName) => {
    const norm = (s) => (s || '').toLowerCase().split('/').pop().replace(/[-_]?v\d+(\.\d+)?$/, '');
    const want = norm(vmName);
    const hit = vmList.find(v => v.name === vmName || norm(v.name) === want);
    return hit ? !!hit.tier_based : false;
  };

  const saveSchedule = async (value) => {
    setSchedule(value); setSavingSched(true);
    try {
      const res = await fetch('/api/challenger-schedule', {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule: value }),
      });
      const data = await res.json().catch(() => ({}));
      setRankMsg(res.ok ? `Schedule set: ${scheduleOpts[value] || value}.` : (data.error || 'Could not set schedule.'));
    } catch (e) { setRankMsg('Network error setting schedule.'); }
    finally { setSavingSched(false); }
  };

  const stage = async () => {
    if (!pick) return;
    await handleChallengerAdd(pick);
    setPick('');
    setTimeout(load, 600);
  };
  const moveToVm = async (c) => {
    const rawId = c.model_id || c.id;
    // If the user explicitly picked a gateway fqn for this challenger, push THAT
    // (guarantees a valid target); else push the raw id and let the backend resolve.
    const mId = targetPick[rawId] || rawId;
    const vm = (c.challenger_info || {}).target_vm || 'echostar/smart-v1';
    // Tier-routed VMs (Auto Routing: Simple/Medium/Complex) take a tier, not a
    // primary weight. Weight-based VMs take role:primary as before.
    const opts = vmIsTier(vm) ? { tier: tierPick[mId] || 'medium' } : { role: 'primary' };
    let st = await handleVmPush(mId, vm, env, opts);
    // If the VM doesn't exist, offer to create it as a NEW weight-based (A/B-ready)
    // VM and retry. Explicit confirm so a wrong name never silently makes junk.
    if (st === 'VM_NOT_FOUND') {
      const ok = window.confirm(
        `Virtual model "${vm}" does not exist in TrueFoundry.\n\n` +
        `Create it as a NEW weight-based virtual model (A/B-ready, so the 10→50→100 ` +
        `flip can run) with ${mId} as the first target at 100%?`);
      if (ok) {
        st = await handleVmPush(mId, vm, env, { role: 'primary', create_if_missing: true });
      }
    }
    // Only clear it from the arena if the push actually landed a placement
    // (SUCCESS/CREATED = live, NOT_CONFIGURED = pending/not-live). On a hard ERROR keep
    // it as a challenger so it doesn't vanish — the status bar shows why.
    if (st === 'SUCCESS' || st === 'CREATED' || st === 'NOT_CONFIGURED') {
      await handleChallengerRemove(mId);
    }
    setTimeout(load, 600);
  };
  const remove = async (c) => { await handleChallengerRemove(c.model_id || c.id); setTimeout(load, 600); };

  // The autonomous ranking agent: rank All Models + EchoStar Approved and keep only
  // the top 1-3 per VM. Runs on a schedule (every minute); this button triggers it now.
  const runRank = async () => {
    setRanking(true); setRankMsg('');
    try {
      const res = await fetch('/api/challenger-rank-run', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setRankMsg(data.error || 'Ranking failed.'); }
      else { setRankMsg(`Ranked — ${ (data.written || []).length } challenger(s) selected, ${ (data.cleared || []).length } dropped.`); setTimeout(load, 400); }
    } catch (e) { setRankMsg('Network error running the ranking agent.'); }
    finally { setRanking(false); }
  };

  const stageable = Array.from(new Set(
    [...(echostarCatalog || []), ...(otherCatalog || [])].map(m => m.model_id || m.id).filter(Boolean)
  ));

  return (
    <div>
      <h3 style={{ marginBottom: '4px' }}>⑤ Challenger arena</h3>
      <p style={{ color: '#666', fontSize: '13px', marginTop: 0 }}>
        Models lined up to <strong>replace a VM incumbent</strong>. Each shows <strong>why it's a challenger</strong> and
        the criteria behind it. EchoStar-approved challengers are already vetted (high score); Other-tab challengers must
        justify the slot; the 4 <strong>self-hosted PoC</strong> (Nemotron/P5e, Gemma & Muse/G7e, Granite/CPU) are always here to
        compare against frontier. Move a winner into the VM, or send it back.
      </p>

      {/* Autonomous ranking agent banner */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: '#f0f7ff', border: '1px solid #b6d4fe', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px' }}>
        <span style={{ fontSize: '12px', color: '#084298' }}>
          🤖 <strong>Auto-maintained.</strong> An agent ranks All Models + EchoStar Approved every minute and keeps only the
          <strong> top 1–3 per VM</strong>. EchoStar-approved models get a vetting bonus; unapproved score lower.
        </span>
        {(can('manage_config')) && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '11px', color: '#084298' }}>Run every:</label>
            <select value={schedule} onChange={e => saveSchedule(e.target.value)} disabled={savingSched}
                    title="How often the ranking agent runs automatically (admin)"
                    style={{ fontSize: '12px', padding: '5px', borderRadius: '4px' }}>
              {Object.keys(scheduleOpts).map(k => <option key={k} value={k}>{scheduleOpts[k]}</option>)}
            </select>
            <button onClick={runRank} disabled={ranking}
                    style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: '#0073bb', color: '#fff', borderRadius: '20px', cursor: ranking ? 'default' : 'pointer', fontWeight: 'bold' }}>
              {ranking ? 'Ranking…' : '▶ Run ranking now'}
            </button>
          </div>
        )}
      </div>
      {rankMsg && <p style={{ fontSize: '12px', color: '#084298', background: '#eef3f8', borderRadius: '4px', padding: '6px 10px' }}>{rankMsg}</p>}

      {can('assign_vm') && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '10px 0', flexWrap: 'wrap' }}>
          <select value={pick} onChange={e => setPick(e.target.value)} style={{ padding: '6px', fontSize: '12px', minWidth: '260px' }}>
            <option value="">Stage a challenger from EchoStar / Other…</option>
            {stageable.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={stage} disabled={!pick} style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: pick ? '#fd7e14' : '#ccc', color: '#fff', borderRadius: '4px', cursor: pick ? 'pointer' : 'default', fontWeight: 'bold' }}>+ Stage challenger</button>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#718096' }}>Promote into env:</span>
          <select value={env} onChange={e => setEnv(e.target.value)} style={{ padding: '6px', fontSize: '12px' }}>
            <option value="sandbox">sandbox</option><option value="np">np</option><option value="prod">prod</option>
          </select>
          <button onClick={load} style={{ padding: '6px 10px', fontSize: '12px', background: '#eef3f8', color: '#0073bb', border: '1px solid #b6d4fe', borderRadius: '4px', cursor: 'pointer' }}>Refresh</button>
        </div>
      )}

      {loading ? <p style={{ color: '#666' }}>Loading challengers…</p> : items.length === 0 ? (
        <div style={{ color: '#888', background: '#fff', border: '1px dashed #cbd5e0', borderRadius: '8px', padding: '22px', textAlign: 'center' }}>
          No challengers staged. Add one above, or from the Other Models tab.
        </div>
      ) : items.map((c, i) => {
        const mId = c.model_id || c.id;
        const info = c.challenger_info || {};
        const crit = info.criteria || {};
        const sb = SOURCE_BADGE[info.source] || SOURCE_BADGE.other;
        const vm = info.target_vm || 'echostar/smart-v1';
        const vmColor = (VM_META[vm] || {}).color || '#4a5568';
        const notReady = info.source === 'self_hosted' && !crit.self_hosted_ready;
        return (
          <div key={i} style={{ border: '1px solid #e2e8f0', borderLeft: `4px solid ${vmColor}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', background: '#fff' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
              {info.rank != null && (
                <span title="Autonomous rank for this VM" style={{ background: vmColor, color: '#fff', borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: 'bold' }}>#{info.rank}</span>
              )}
              <strong style={{ fontSize: '13px', wordBreak: 'break-all' }}>{c.display_name || mId}</strong>
              <span style={{ background: sb.bg, color: sb.fg, borderRadius: '10px', padding: '1px 8px', fontSize: '10px', fontWeight: 'bold' }}>{sb.label}</span>
              <span style={{ fontSize: '11px', color: vmColor, fontWeight: 'bold' }}>→ {vm}</span>
              {crit.challenger_score != null && (
                <span title="Challenger score = blended + approval bonus" style={{ fontSize: '10px', color: '#084298', fontWeight: 'bold' }}>score {crit.challenger_score}</span>
              )}
              {crit.vetted === true && <span title="EchoStar-approved: vetting bonus applied" style={{ fontSize: '10px', color: '#155724', background: '#d4edda', borderRadius: '8px', padding: '1px 6px' }}>vetted +{crit.approved_bonus}</span>}
              {crit.vetted === false && <span title="Not vetted by AI Office: no bonus" style={{ fontSize: '10px', color: '#856404', background: '#fff3cd', borderRadius: '8px', padding: '1px 6px' }}>unvetted</span>}
              {info.auto && <span title="Selected by the ranking agent" style={{ fontSize: '10px', color: '#0b5cad' }}>🤖 auto</span>}
              {c.tier_category && <span style={{ fontSize: '10px', color: '#555', border: '1px solid #ddd', borderRadius: '8px', padding: '1px 6px' }}>{c.tier_category}</span>}
              {c.hardware && <span style={{ fontSize: '10px', color: '#0b5cad' }}>{c.hardware}</span>}
              {notReady && <span style={{ fontSize: '10px', color: '#b35c00', background: '#fff3cd', borderRadius: '8px', padding: '1px 6px' }}>endpoint not live</span>}
            </div>

            {/* WHY it's a challenger */}
            <div style={{ fontSize: '12px', color: '#333', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '7px 9px', marginBottom: '6px' }}>
              <strong>Why: </strong>{info.reason || 'Staged for comparison.'}
            </div>

            {/* CRITERIA detail */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '11px', color: '#555', marginBottom: '8px' }}>
              {crit.challenger_blended != null && <span>blended <strong>{crit.challenger_blended}</strong></span>}
              {crit.incumbent_blended != null && <span>vs incumbent <strong>{crit.incumbent_blended}</strong>{crit.incumbent ? ` (${crit.incumbent})` : ''}</span>}
              {crit.score_delta != null && <span>Δscore <strong style={{ color: crit.score_delta >= 0 ? '#155724' : '#b00020' }}>{crit.score_delta >= 0 ? '+' : ''}{crit.score_delta}</strong></span>}
              {crit.challenger_cost_per_1m != null && <span>cost <strong>${crit.challenger_cost_per_1m}</strong>/1M</span>}
              {crit.cost_delta_pct != null && <span>Δcost <strong style={{ color: crit.cost_delta_pct <= 0 ? '#155724' : '#b00020' }}>{crit.cost_delta_pct}%</strong></span>}
            </div>

            {/* Gateway target readiness — which real fqn this challenger pushes to.
                Self-hosted PoC are not gateway models, so skip the check for them. */}
            {info.source !== 'self_hosted' && (() => {
              const tr = resolveTarget(targetPick[mId] || mId);
              if (tr.status === 'MATCH' || tr.status === 'RESOLVED')
                return <div style={{ fontSize: '11px', color: '#155724', background: '#d4edda', border: '1px solid #b7dfc3', borderRadius: '4px', padding: '4px 8px', marginBottom: '6px' }}>✅ Enabled in TrueFoundry — ready to add to VM: <code>{tr.target}</code></div>;
              if (tr.status === 'AMBIGUOUS' || tr.status === 'NOT_AVAILABLE')
                return (
                  <div style={{ fontSize: '11px', color: '#9a5a1b', marginBottom: '6px' }}>
                    {tr.status === 'NOT_AVAILABLE' ? '⚠ Not enabled in TrueFoundry — pick an enabled model, or enable this one first:' : '⚠ Multiple matches — pick the exact model:'}
                    <select value={targetPick[mId] || ''} onChange={e => setTargetPick(p => ({ ...p, [mId]: e.target.value }))}
                            style={{ fontSize: '11px', padding: '3px', borderRadius: '4px', marginLeft: '6px', maxWidth: '320px' }}>
                      <option value="">{tr.status === 'NOT_AVAILABLE' ? '(choose a gateway model…)' : '(choose one…)'}</option>
                      {(tr.candidates || gatewayModels).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    {tr.status === 'NOT_AVAILABLE' && (
                      <details style={{ marginTop: '6px' }}>
                        <summary style={{ cursor: 'pointer', color: '#0073bb' }}>📋 Guide: enable this model in TrueFoundry</summary>
                        <div style={{ marginTop: '6px', color: '#444' }}>
                          <p style={{ margin: '4px 0' }}>
                            Add this entry to the provider account's <code>integrations</code> list via
                            TrueFoundry → Models → <strong>Add Model</strong> → <strong>Apply using YAML</strong>.
                            <strong> Keep every existing model in the list</strong> and leave <code>auth_data.api_key</code> set —
                            enabling re-applies the whole account manifest, so dropping either breaks it.
                          </p>
                          <pre style={{ background: '#1e1e1e', color: '#d4d4d4', padding: '8px', borderRadius: '4px', overflowX: 'auto', fontSize: '10px', whiteSpace: 'pre' }}>{manifestEntryFor(mId)}</pre>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button type="button" onClick={() => { try { navigator.clipboard && navigator.clipboard.writeText(manifestEntryFor(mId)); } catch (e) {} }}
                                    style={{ fontSize: '11px', padding: '3px 8px', cursor: 'pointer' }}>Copy manifest entry</button>
                            <a href={TF_MODELS_URL} target="_blank" rel="noreferrer" style={{ fontSize: '11px' }}>Open TrueFoundry Models ↗</a>
                          </div>
                        </div>
                      </details>
                    )}
                  </div>
                );
              return null;
            })()}

            {can('assign_vm') && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {vmIsTier(vm) && (
                  <select value={tierPick[mId] || 'medium'} onChange={e => setTierPick(p => ({ ...p, [mId]: e.target.value }))}
                          title="This VM uses Auto Routing — pick the complexity tier this model serves"
                          style={{ fontSize: '11px', padding: '4px', borderRadius: '4px' }}>
                    <option value="simple">Simple tier</option>
                    <option value="medium">Medium tier</option>
                    <option value="complex">Complex tier</option>
                  </select>
                )}
                <button onClick={() => moveToVm(c)} disabled={notReady}
                        title={notReady ? 'Self-hosted endpoint is not live yet'
                                        : (vmIsTier(vm) ? `Add to ${vm} (${env}) at the ${tierPick[mId] || 'medium'} tier`
                                                        : `Push into ${vm} (${env}) as primary`)}
                        style={{ padding: '5px 11px', fontSize: '11px', border: 'none', background: notReady ? '#ccc' : vmColor, color: '#fff', borderRadius: '4px', cursor: notReady ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}>
                  → Move to TrueFoundry VM
                </button>
                {info.source !== 'self_hosted' && (
                  <button onClick={() => remove(c)}
                          style={{ padding: '5px 11px', fontSize: '11px', border: '1px solid #dc3545', background: '#fff5f5', color: '#c53030', borderRadius: '4px', cursor: 'pointer' }}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
